// src/components/ui/GalleryCard.tsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ZoomIn, Tag, ImageOff, Loader } from 'lucide-react';
import { scaleUp } from '../../utils/animations';

interface GalleryCardProps {
  image: {
    id: string;
    src: string;
    alt: string;
    category: string;
    description: string;
  };
  onClick: () => void;
}

const GalleryCard: React.FC<GalleryCardProps> = ({ image, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <motion.div
      variants={scaleUp}
      className="relative overflow-hidden rounded-xl cursor-pointer group shadow-lg hover:shadow-xl transition-shadow duration-300"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      whileHover={{ y: -5 }}
    >
      {/* Image Container */}
      <div className="aspect-square bg-gradient-to-br from-mauve-50 to-mauve-100 relative">
        {/* Loading Skeleton */}
        {!imgLoaded && !imgError && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader className="animate-spin text-mauve-300" size={24} />
          </div>
        )}

        {/* Actual Image */}
        {!imgError ? (
          <img
            src={image.src}
            alt={image.alt}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              imgLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImgLoaded(true)}
            onError={() => {
              setImgError(true);
              setImgLoaded(true);
              console.error(`Failed to load image: ${image.src}`);
            }}
            loading="lazy"
          />
        ) : (
          /* Fallback when image fails to load */
          <div className="w-full h-full flex flex-col items-center justify-center p-4">
            <ImageOff className="text-mauve-300 mb-3" size={40} />
            <p className="text-mauve-700 text-center text-sm font-medium mb-1">{image.category}</p>
            <p className="text-mauve-500 text-xs text-center">{image.description.substring(0, 60)}...</p>
          </div>
        )}

        {/* Debug info (remove in production) */}
        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
          {image.src.split('/').pop()}
        </div>

        {/* Hover Overlay */}
        <div
          className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent flex items-end p-4 transition-all duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="text-white">
            <ZoomIn size={24} className="mb-2" />
            <p className="text-sm font-medium">View Details</p>
          </div>
        </div>
      </div>

      {/* Category Badge */}
      <div className="absolute top-3 left-3">
        <div className="flex items-center bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm">
          <Tag size={12} className="text-mauve-500 mr-1" />
          <span className="text-xs font-medium text-mauve-700">{image.category}</span>
        </div>
      </div>

      {/* Description (always visible) */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-4">
        <p className="text-white text-sm font-medium line-clamp-2">{image.description}</p>
      </div>

      {/* View Button (appears on hover) */}
      <div
        className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300 ${
          isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
        }`}
      >
        <button className="bg-white text-mauve-700 px-4 py-2 rounded-lg font-medium shadow-lg hover:bg-mauve-50 transition-colors flex items-center">
          <ZoomIn size={16} className="mr-2" />
          View Design
        </button>
      </div>
    </motion.div>
  );
};

export default GalleryCard;